// ── QAPolicySettingsPage ──────────────────────────────────────────────────
//
// QA.A3 (#950) — tenant-level QA policy editor. Wraps the shared QAPolicyForm
// (also used by the onboarding wizard) on a standalone settings surface so
// admins can edit the policy after onboarding. Backend CRUD shipped in QA.A1
// (#948); this page is the missing UI.

import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import QAPolicyForm from '../components/qa/QAPolicyForm';
import { HelpPopover } from '../components/help';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 20,
  marginBottom: 16,
};

export default function QAPolicySettingsPage() {
  const { myTenant, loading } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, padding: 32, maxWidth: 760 }}>
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/settings"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              textDecoration: 'none',
            }}
          >
            ← Back to settings
          </Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            QA Policy
          </h1>
          <HelpPopover
            id="qa-policy-settings"
            variant="info"
            heading="QA Policy"
            body={
              <>
                <p style={{ margin: '0 0 8px' }}>
                  The QA Agent reads this policy at the start of every QA session. The pass rate
                  threshold determines the verdict: sessions below it are marked FAIL.
                </p>
                <p style={{ margin: 0 }}>
                  Changes apply tenant-wide and take effect on the next QA session. Only tenant
                  admins can edit.
                </p>
              </>
            }
          />
        </div>
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--text-secondary)',
            marginBottom: 24,
          }}
        >
          Configure the quality assurance standards, pass rate threshold, and release gates that the
          QA Agent enforces across your organisation.
        </p>

        {loading || !tenantId ? (
          <div style={{ ...cardStyle, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        ) : (
          <div style={cardStyle}>
            <QAPolicyForm
              tenantId={tenantId}
              canEdit={isAdmin}
              showIntro={false}
              submitLabel="Save changes"
            />
          </div>
        )}
      </main>
    </div>
  );
}
